import localforage from "localforage";
import Swal from "sweetalert2";
import { Attribute, ImageType } from "../types";
import {
  DownloadImages,
  isDownloadImage,
  isImage,
  isValidImageResponse,
  startBulkDownloadWithAlerts,
} from "./images.model";

//Validation function for filters
function isValidDateValue(value: unknown): value is Attribute {
  if (!isObject(value)) {
    console.log("Value is not an object");
    return false;
  }
  return true;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

//Function to validate images

interface SharedFiltersResponse {
  statusCode: number;
  response: Attribute;
  updated_at?: string;
}

export async function fetchSharedCachedFilters(
  sharedBoardToken: string,
  boardID: string
): Promise<SharedFiltersResponse | string> {
  console.log("Fetching shared filters");

  const getSharedFilters: SharedFiltersResponse | null =
    await localforage.getItem(`filters-shared-${boardID}`);
  try {
    if (
      getSharedFilters === null ||
      getSharedFilters === undefined ||
      typeof getSharedFilters === "string"
    ) {
      console.log("No shared filters in local storage");
      return await fetchAndCacheFilters(sharedBoardToken, boardID);
    } else {
      const filterObject = getSharedFilters;
      if (
        filterObject &&
        filterObject.statusCode &&
        filterObject.statusCode === 200
      ) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/shared/board/precache/refresh`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              sharedBoardToken,
            }),
          }
        );

        if (response.status === 200) {
          const data = await response.json();
          // validate
          if (data.updated_at !== filterObject.updated_at) {
            console.log("Updating filters");
            return await fetchAndCacheFilters(sharedBoardToken, boardID);
          }
          console.log("Filters are up to date");
        }
      }

      return {
        statusCode: 200,
        response: filterObject.response,
      } as SharedFiltersResponse;
    }
  } catch (error) {
    console.log("Error fetching shared filters", error);
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: "Something went wrong",
    });
    return "Error fetching shared filters";
  }
}

async function fetchAndCacheFilters(
  sharedBoardToken: string,
  boardID: string
): Promise<SharedFiltersResponse | string> {
  const controller = new AbortController();
  const signal = controller.signal;
  const timeoutID = setTimeout(() => controller.abort(), 180000);

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/shared/board/precache`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        sharedBoardToken,
      }),
      signal,
    }
  );
  clearTimeout(timeoutID);
  if (response.status === 200) {
    const data = await response.json();
    data.statusCode = 200;

    const attributeData = data.response as Attribute;
    if (isValidDateValue(data)) {
      // add status code to data
      localforage.setItem(`filters-shared-${boardID}`, data);

      return { statusCode: 200, response: attributeData };
    } else {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Failed to validate data",
      });
      return "Failed to validate data";
    }
  } else if (response.status === 204) {
    return { statusCode: 204, response: {} } as SharedFiltersResponse;
  } else if (response.status === 206) {
    return { statusCode: 206, response: {} } as SharedFiltersResponse;
  } else {
    console.log("Failed to get a response");
    return "Failed to get a response";
  }
}

export async function fetchingSharedImages(
  sharedBoardToken: string,
  boardID: string
): Promise<ImageType[]> {
  console.log("Fetching shared images");
  // Removed local storage for shared images - user does not navigate away from shared board while in app
  // const getLocalImages: string | null = await localforage.getItem(
  //   `shared-board-images-${boardID}`
  // );

  try {
    // if (getLocalImages === null) {
    // fetch images
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/shared/board/get`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sharedBoardToken,
          boardID,
        }),
      }
    );

    if (response.status === 200) {
      const data = await response.json();
      if (isValidImageResponse(data)) {
        // localforage.setItem(
        //   `shared-board-images-${boardID}`,
        //   JSON.stringify(data)
        // );
        console.log("Data", data);
        return data.images;
      } else {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Failed to validate share boards",
        });
        console.log("Failed to validate ");
        return [] as ImageType[];
      }
    } else if (response.status === 204) {
      return [] as ImageType[];
    } else {
      console.log("Failed to get a response");
      return [] as ImageType[];
    }
    // } else {
    //   console.log("Setting images from local storage");
    //   let localImages = JSON.parse(getLocalImages);
    //   return localImages.images;
    // }
  } catch (error) {
    console.log("Error fetching shared images", error);
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: "Something went wrong",
    });
    return [];
  }
}

export async function fetchSingleShareImage(
  sourceID: string,
  imageID: string
): Promise<ImageType | string> {
  const sharedBoardToken: string | null = await localforage.getItem(
    "sharedToken"
  );

  if (sharedBoardToken === null || sharedBoardToken === undefined) {
    Swal.fire({
      icon: "error",
      title: "Invalid URL",
      text: "The URL is invalid",
    });
    return "Invalid URL";
  }
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/shared/board/image/get`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          boardId: sourceID,
          imageID,
          sharedBoardToken,
        }),
      }
    );

    if (response.status === 200) {
      const data = await response.json();
      if (isImage(data)) {
        return data;
      } else {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Failed to validate data",
        });
        return "Failed to validate data";
      }
    } else if (response.status === 204) {
      return "No content found";
    } else {
      console.log("Failed to get a response");
      return "Failed to get a response";
    }
  } catch (error) {
    console.log("Error fetching single image", error);
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: "Something went wrong",
    });
    return "Failed to fetch image";
  }
}

export async function fetchSharedBoardBulkImageDownload(
  imageList: string[]
): Promise<string> {
  try {
    const sharedBoardToken: string | null = await localforage.getItem(
      "sharedToken"
    );

    if (sharedBoardToken === null || sharedBoardToken === undefined) {
      Swal.fire({
        icon: "error",
        title: "Invalid URL",
        text: "The URL is invalid",
      });
      console.error("Shared board token is null or undefined");
      return "The shared board token is invalid.";
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/shared/board/image/download/bulk`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          imageList,
          sharedBoardToken,
        }),
      }
    );

    if (response.status === 200) {
      const data = await response.json();

      console.log("Images downloaded successfully");
      if (isDownloadImage(data)) {
        const downloadImages = data as DownloadImages;
        console.log(downloadImages);
        const downloadLinks = downloadImages.imageFiles.map((img) => {
          return {
            img_guid: img.img_guid,
            img_title: img.img_title,
          };
        });
        startBulkDownloadWithAlerts(downloadLinks);
        return "success";
      } else {
        console.log("Invalid image data received");
        Swal.fire({
          title: "Download Failed",
          text: "Invalid image data received.",
          icon: "error",
        });
        return "Invalid image data received.";
      }
    } else {
      console.log("Failed to download images");
      Swal.fire({
        title: "Download Failed",
        text: "Failed to download images from server.",
        icon: "error",
      });
      return "Failed to download images from server.";
    }
  } catch (error) {
    console.log("Error fetching bulk download", error);
    Swal.fire({
      title: "Download Failed",
      text: "An error occurred while downloading shared board images.",
      icon: "error",
    });
    return "An error occurred while downloading shared board images.";
  }
}
